import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react"
import { TableHead } from "@/components/ui/table"
import { cn } from "@/lib/utils"
import type { SortDirection } from "@/hooks/useSortableData"

interface SortableTableHeadProps<K extends string> {
  label: string
  sortKey: K
  currentSortKey: K
  direction: SortDirection
  onSort: (key: K) => void
  className?: string
}

function SortIcon<K extends string>({
  sortKey,
  currentKey,
  direction,
}: {
  sortKey: K
  currentKey: K
  direction: SortDirection
}) {
  if (sortKey !== currentKey) {
    return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />
  }
  return direction === "asc" ? (
    <ArrowUp className="w-4 h-4 ml-1" />
  ) : (
    <ArrowDown className="w-4 h-4 ml-1" />
  )
}

function getAriaSort<K extends string>(
  sortKey: K,
  currentKey: K,
  direction: SortDirection
): "ascending" | "descending" | "none" {
  if (sortKey !== currentKey) return "none"
  return direction === "asc" ? "ascending" : "descending"
}

export function SortableTableHead<K extends string>({
  label,
  sortKey,
  currentSortKey,
  direction,
  onSort,
  className,
}: SortableTableHeadProps<K>) {
  const ariaSort = getAriaSort(sortKey, currentSortKey, direction)
  const isActive = sortKey === currentSortKey
  const nextDirection = isActive && direction === "asc" ? "descendente" : "ascendente"

  return (
    <TableHead className={className} aria-sort={ariaSort}>
      <button
        type="button"
        className={cn(
          "flex items-center hover:text-foreground transition-colors cursor-pointer",
          isActive && "text-foreground"
        )}
        onClick={() => onSort(sortKey)}
        aria-label={`Ordenar por ${label} ${nextDirection}`}
      >
        {label}
        <SortIcon sortKey={sortKey} currentKey={currentSortKey} direction={direction} />
      </button>
    </TableHead>
  )
}
